import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { Button, Card, Row, theme } from 'antd';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import type { ParameterSelection_RequestableParameterFragment, ParameterToAddInput } from '../../../../graphql/generated/graphql.ts';
import type { SheetData, SheetGroup } from '../../../../graphql/sheet';
import type { SelectedParamInfo } from '../Sheets.tsx';
import { MemoSheetParameterCol } from './SheetParameterCol.tsx';

export const SheetGroupCard: React.FC<{
  sheet: SheetData;
  group: SheetGroup;
  sheetParamLookup: Record<string, ParameterSelection_RequestableParameterFragment | undefined>;
  selectedParamInfo: SelectedParamInfo;
  onAdd: (inputs: ParameterToAddInput[]) => void;
  onRemove: (paramId: string) => void;
  onInfo: (param: ParameterSelection_RequestableParameterFragment) => void;
  reorder: boolean;
  flipParamNames: boolean;
  highlight: string;
}> = ({ sheet, group, sheetParamLookup, selectedParamInfo, onAdd, onRemove, onInfo, reorder, flipParamNames, highlight }) => {
  const { token } = theme.useToken();

  const isAvailable = useCallback(
    (requestableParam?: ParameterSelection_RequestableParameterFragment) =>
      requestableParam?.id && !selectedParamInfo.withoutParameterIds[requestableParam.id],
    [selectedParamInfo]
  );

  const addAll = useCallback(() => {
    onAdd(
      group.parameters
        .map(sheetParam => sheetParamLookup[sheetParam.id])
        .filter(param => isAvailable(param))
        .map(param => ({
          parameterId: param!.id,
          preferredBillingType: null,
          preferredDiagnoseIds: [],
        }))
    );
  }, [group, isAvailable, onAdd, sheetParamLookup]);

  const style = useMemo(
    () => css`
      margin-top: ${token.marginSM}px;

      &:first-child {
        margin-top: 0;
      }
    `,
    [token]
  );

  return (
    <Card
      title={highlight ? <Markable tokens={highlight}>{group.name}</Markable> : group.name}
      type="inner"
      size="small"
      className={style}
      extra={
        <Button type="link" size="small" onClick={addAll}>
          Alle auswählen
        </Button>
      }
    >
      <Row gutter={[16, 0]}>
        {group.parameters.map((sheetParam, index) => {
          const requestableParam = sheetParamLookup[sheetParam.id];
          const selectedParamId = selectedParamInfo.selectedParamMainIds[sheetParam.id];
          const available = !!requestableParam;
          const compatible = available && !selectedParamInfo.withoutParameterIds[requestableParam.id];

          return (
            <MemoSheetParameterCol
              key={index}
              sheet={sheet}
              group={group}
              sheetParam={sheetParam}
              requestableParam={requestableParam}
              available={available}
              compatible={compatible}
              selected={!!selectedParamId}
              onAdd={onAdd}
              onRemove={onRemove}
              onInfo={onInfo}
              reorder={reorder}
              flipParamNames={flipParamNames}
              highlight={highlight}
            />
          );
        })}
      </Row>
    </Card>
  );
};
